import clsx from "clsx"
import { DateTime } from "luxon"
import React, { MouseEvent, useCallback, useMemo, useState } from "react"
import Card from "shared/Components/Card/Card"
import {
    Box,
    ButtonGroup,
    CardActionArea,
    CardContent,
    CardMedia,
    Chip,
    Collapse,
    Fade,
    Grid,
    IconButton,
    makeStyles,
    Typography,
} from "@material-ui/core"
import { Localized, useLocalization } from "@fluent/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHeart as faHeartHollow } from "@fortawesome/pro-light-svg-icons/faHeart"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons/faInfoCircle"
import { faMinus } from "@fortawesome/pro-light-svg-icons/faMinus"
import { faPlus } from "@fortawesome/pro-light-svg-icons/faPlus"
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart"
import { useDispatch } from "react-redux"
import { IFoodOrderProduct } from "shared/Modules/Product/productTypes"
import Button from "shared/Components/Button"
import { LoadingIconButton } from "shared/Components/Loading/LoadingButton"
import addProductToFavorites from "mobile/Api/AddProductToFavorites"
import { DiscountType, OfferDisplayLabelType, WebshopType } from "shared/Types/appTypes"
import { useEnvironment } from "shared/Modules/Environment/envHooks"
import { useToken } from "shared/Modules/Login/useToken"
import removeProductFromFavorites from "mobile/Api/RemoveProductToFavorites"
import { openSnackbar } from "../../../Actions/snackbarActions"
import { getProductFavorites } from "../../../Actions/canteenActions"
import { useApiCall } from "shared/Modules/Query/useApiCall"
import { ProductDisabledCover } from "./ProductUnavailable"
import { OrderLine } from "shared/Modules/Basket/basketTypes"
import { toDinero, toDineroOrUndefined } from "shared/Helpers/CurrencyHelper"
import { StandardButton } from "shared/Components/Button/StandardButton"
import { useMoney } from "shared/Modules/Localization/useMoney"
import { OfferTag } from "mobile/Screens/OrderLunch/components/OfferTag"
import { OnlyXLeftTag } from "mobile/Screens/OrderLunch/components/OnlyXLeftTag"
import { LocalizedEnum, LocalizedStrict } from "shared/Modules/Localization/Components/Localized"

const useStyles = makeStyles((theme) => ({
    cardPreviewImage: {
        minWidth: "45%",
        height: "fit-content",
        position: "relative",
        padding: theme.spacing(2, 2, 1.75, 2),
    },
    cardPreviewImageMedia: {
        height: 115,
        borderRadius: 5,
    },
    cardPreviewEmpty: {
        height: 135,
    },
    cardDescription: {
        marginTop: theme.spacing(),
    },
    collapsedCardDescription: {
        height: 40,
        overflow: "hidden",
    },
    cardActions: {
        width: "100%",
    },
    cardActionsContent: {
        padding: theme.spacing(0, 2, 2, 2),
    },
    cardActionText: {
        marginTop: 5,
    },
    cardPickupText: {
        marginLeft: theme.spacing(),
        marginRight: theme.spacing(),
    },
    disabled: {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
    disabledImage: {
        filter: "opacity(60%) grayscale(20%)",
    },
    fullWidth: {
        height: "100%",
        width: "100%",
    },
    button: {
        width: 120,
        height: 36,
    },
    cardActionArea: {
        height: "100%",
    },
    amountLeft: {
        marginTop: theme.spacing(2.5),
        marginLeft: theme.spacing(),
    },
    inBasket: {
        border: "3px solid #0090FF",
        borderRadius: "0.75rem",
    },
    notInBasket: {
        border: "1px solid #CBD5E1",
        borderRadius: "0.75rem",
    },
    cardCoverContainer: {
        position: "relative",
    },
    productName: {
        // overflow: "hidden",
        // maxHeight: 48,
        padding: 0,
        lineHeight: 1.2,
    },
    cardContentModifier: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: theme.spacing(2, 2, 1.5, 2),
        width: "100%",
    },
    collapsedDescription: {
        marginBottom: theme.spacing(1),
    },
    buttonCollapsed: {
        marginTop: "1rem",
    },
    priceRow: {
        display: "flex",
        alignItems: "baseline",
        gap: 8,
    },
    newPrice: {
        color: theme.palette.primary.main,
        fontWeight: 400,
    },
    oldPrice: {
        color: "#64748B",
        fontWeight: 400,
        textDecoration: "line-through",
    },
    newPriceWithDiscount: {
        color: "#DC2626",
        fontWeight: 400,
    },
    marketingTag: {
        background: "#22C55E",
        color: "#fff",
        fontWeight: 500,
        fontSize: "0.75rem",
        padding: "0.375rem",
        borderRadius: 5,
        width: "fit-content",
        position: "absolute",
        left: "0rem",
        bottom: "2rem",
    },
    marketingTagCollapsed: {
        left: "0.75rem",
        bottom: "0.75rem",
    },
    paymentTag: {
        background: "#FEF3C7",
        color: "#78350F",
        fontWeight: 400,
        fontSize: "0.75rem",
        width: "fit-content",
        borderRadius: "1rem",
        padding: "0.25rem 0.75rem",
        lineHeight: 1.6,
    },
    secondaryInfo: {
        padding: "0.375rem 1rem 1rem",
        display: "flex",
        justifyContent: "end",
    },
    description: {
        color: "#00000099",
        lineHeight: 1.25,
        fontWeight: 400,
    },
    allergens: {
        marginTop: "0.25rem",
        color: "#00000099",
    },
    collapseImage: {
        position: "relative",
    },
    dashedDivider: {
        borderTop: "1px dashed #E2E8F0",
        width: "100%",
    },
    dashedDividerContainer: {
        padding: "0 1rem",
    },
    card: {
        boxShadow: "none !important",
        borderRadius: "0.75rem",
    },
    cardSmallerRadius: {
        borderRadius: "0.563rem !important", // as inBasket border is 3px wide - we reduce radius by 3px to keep same look
    },
    gridNoPadding: {
        padding: "0 !important",
    },
    cardMainContent: {
        display: "grid",
        gap: "0.5rem",
    },
    slateButton: {
        fontWeight: 500,
        fontSize: "0.813rem !important",
        backgroundColor: "#0F172A",
    },
    slateButtonOutlined: {
        fontWeight: 500,
        fontSize: "0.813rem !important",
        borderColor: "#0F172A !important",
    },
    addMoreProductButton: {
        padding: "0.75rem 0.875rem",
    },
    addProductButton: {
        maxHeight: "1.875rem",
        padding: "0.25rem 3rem",
    },
    productCardContent: {
        margin: 0,
    },
}))

interface IProps {
    expanded: boolean
    onClick: (productId: number) => void
    orderLine?: OrderLine
    updateOrderLine: (productId: number, date: DateTime, orderLine: OrderLine) => void
    removeOrderLine: (productId: number, date: DateTime) => void
    product: IFoodOrderProduct
    date: DateTime
    isTakeaway: boolean
    isMeetingPlanning: boolean
}

function OrderLunchProduct(props: IProps) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const token = useToken()
    const { currentEnv } = useEnvironment()
    const { l10n } = useLocalization()
    const [favorited, setFavorited] = useState(() => {
        if (props.product.userDetails && "favoriteId" in props.product.userDetails)
            return { state: true, id: props.product.userDetails.favoriteId }
        else return { state: false }
    })

    const {
        loading: isAddingFavorite,
        callForResult: callAddFavorite,
        handleCallError,
    } = useApiCall(addProductToFavorites)
    const { loading: isRemovingFavorite, callForAction: callRemoveFavorite } = useApiCall(removeProductFromFavorites)

    const orderLine = props?.orderLine ?? {
        companyItems: 0,
        privateItems: 0,
        pricePerItem: toDineroOrUndefined(props.product.price),
    }

    function updateCompanyItems(amount: number) {
        if (props.product?.orderDetails?.productLeftInStock === orderLine.privateItems + (amount - 1)) {
            dispatch(openSnackbar(l10n.getString("order-food-sold-out-snackbar", null, "Varen er udsolgt")))
            return
        }

        if (amount + orderLine.privateItems === 0) props.removeOrderLine(props.product.id, props.date)
        else props.updateOrderLine(props.product.id, props.date, { ...orderLine, companyItems: amount })
    }

    function updatePrivateItems(amount: number) {
        if (props.product?.orderDetails?.productLeftInStock === orderLine.companyItems + (amount - 1)) {
            dispatch(openSnackbar(l10n.getString("order-food-sold-out-snackbar", null, "Varen er udsolgt")))
            return
        }

        if (amount + orderLine.companyItems === 0) props.removeOrderLine(props.product.id, props.date)
        else props.updateOrderLine(props.product.id, props.date, { ...orderLine, privateItems: amount })
    }

    function getButton(amount: number, onUpdate: (amount: number) => void, isOutlined?: boolean) {
        if (!amount) {
            return (
                <Localized id="order-food-add">
                    <Button
                        onClick={() => onUpdate(1)}
                        color="inherit"
                        variant={isOutlined ? "outlined" : "contained"}
                        borderRadius={12}
                        textColor={isOutlined ? "#0F172A" : "#ffffff"}
                        className={clsx(
                            classes.button,
                            classes.addProductButton,
                            isOutlined ? classes.slateButtonOutlined : classes.slateButton
                        )}
                    >
                        Tilføj
                    </Button>
                </Localized>
            )
        } else {
            return (
                <ButtonGroup variant="outlined" color="inherit" className={classes.button}>
                    <Button
                        onClick={() => onUpdate(amount - 1)}
                        variant={isOutlined ? "outlined" : "contained"}
                        borderRadius={12}
                        textColor={isOutlined ? "#0F172A" : "#ffffff"}
                        className={clsx(
                            classes.fullWidth,
                            classes.addMoreProductButton,
                            isOutlined ? classes.slateButtonOutlined : classes.slateButton
                        )}
                    >
                        <FontAwesomeIcon icon={faMinus} size="lg" />
                    </Button>
                    <Button disabled textColor="#0F172A" className={classes.addMoreProductButton}>
                        {amount}
                    </Button>
                    <Button
                        onClick={() => onUpdate(amount + 1)}
                        variant={isOutlined ? "outlined" : "contained"}
                        borderRadius={12}
                        textColor={isOutlined ? "#0F172A" : "#ffffff"}
                        className={clsx(
                            classes.fullWidth,
                            classes.addMoreProductButton,
                            isOutlined ? classes.slateButtonOutlined : classes.slateButton
                        )}
                    >
                        <FontAwesomeIcon icon={faPlus} size="lg" />
                    </Button>
                </ButtonGroup>
            )
        }
    }

    const availability = props.product.orderDetails?.availability
    const disabled = !(availability?.isAvailable ?? true)
    const inBasket = orderLine.companyItems || orderLine.privateItems
    const cardClasses = clsx({
        [classes.inBasket]: inBasket,
        [classes.notInBasket]: !orderLine.companyItems && !orderLine.privateItems,
        [classes.cardCoverContainer]: disabled,
    })

    function addToFavorite(event: MouseEvent) {
        event.stopPropagation()

        callAddFavorite(
            props.product.id,
            props.isTakeaway ? WebshopType.TAKE_AWAY : WebshopType.LUNCH,
            token,
            currentEnv
        )
            .then((data) => {
                if (data.product.userDetails && "favoriteId" in data.product.userDetails) {
                    setFavorited({ state: true, id: data.product.userDetails.favoriteId })
                    dispatch(getProductFavorites())
                }
            })
            .catch((e) => handleCallError(e, "adding favorite"))
    }

    function removeFromFavorite(event: MouseEvent) {
        event.stopPropagation()

        if (!favorited.id) return

        callRemoveFavorite(favorited.id, props.isTakeaway ? WebshopType.TAKE_AWAY : WebshopType.LUNCH, token)
            .then(() => {
                setFavorited({ state: false })
                dispatch(getProductFavorites())
            })
            .catch((e) => handleCallError(e, "removing favorite"))
    }

    function getFavoriteButton() {
        if (favorited.state) {
            return (
                <LoadingIconButton
                    size="small"
                    color="primary"
                    onClick={removeFromFavorite}
                    loading={isRemovingFavorite}
                >
                    <FontAwesomeIcon icon={faHeart} />
                </LoadingIconButton>
            )
        }
        return (
            <LoadingIconButton size="small" onClick={addToFavorite} loading={isAddingFavorite}>
                <FontAwesomeIcon icon={faHeartHollow} />
            </LoadingIconButton>
        )
    }

    const handleQuickAdd = useCallback(() => {
        if (props?.isMeetingPlanning) {
            updateCompanyItems(orderLine.companyItems + 1)
            return
        }
        updatePrivateItems(orderLine.privateItems + 1)
    }, [props?.isMeetingPlanning])

    const moneyFactory = useMoney()

    const oldNewPrice = useMemo(() => {
        const priceFmt = props.product?.price?.formatted
        const offer = props.product?.offerDetails
        const display = offer?.discount?.displayLabelType

        if (
            display === OfferDisplayLabelType.OLD_NEW_PRICE &&
            props.product.offerDetails?.discount?.type === DiscountType.DISCOUNT_PRICE &&
            props.product.offerDetails?.discount?.originalPrice
        ) {
            return {
                price: priceFmt,
                oldPrice: moneyFactory.format(toDinero(props.product.offerDetails?.discount?.originalPrice)),
            }
        }
        return { price: priceFmt }
    }, [moneyFactory, props.product])

    const showOfferTag =
        props.product.offerDetails?.discount &&
        props.product.offerDetails.discount.displayLabelType !== OfferDisplayLabelType.OLD_NEW_PRICE
    const paymentMethod = props.product?.paymentDetails?.method
    const allergens = props.product?.productDescription?.allergens?.map(({ name }) => name)
    const allergensList = allergens?.join(", ")

    const hasMoreInfo = showOfferTag || paymentMethod

    return (
        <Card className={cardClasses} cardClassName={clsx(classes.card, { [classes.cardSmallerRadius]: inBasket })}>
            <CardActionArea
                onClick={disabled ? undefined : () => props.onClick(props.product.id)}
                disableRipple={disabled}
                component="div"
                className={classes.cardActionArea}
            >
                {props.product?.imageUrl && (
                    <Collapse in={props.expanded} className={classes.collapseImage}>
                        <CardMedia image={props.product.imageUrl} style={{ height: 130 }} />
                        {props.product?.productDescription?.marketingTag && (
                            <Box className={clsx([classes.marketingTag, classes.marketingTagCollapsed])}>
                                {props.product?.productDescription?.marketingTag}
                            </Box>
                        )}
                        {!showOfferTag && <OfferTag top="0.5rem" right="0.5rem" product={props.product} />}
                    </Collapse>
                )}
                <Grid
                    container
                    justifyContent="space-between"
                    wrap="nowrap"
                    className={clsx({ [classes.disabled]: disabled })}
                >
                    <CardContent className={classes.cardContentModifier}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            className={classes.productCardContent}
                        >
                            <Grid
                                item
                                className={clsx(classes.fullWidth, classes.gridNoPadding, classes.cardMainContent)}
                            >
                                {props.expanded ? (
                                    <Grid container item justifyContent="space-between">
                                        <Grid item style={{ width: "80%" }}>
                                            <Typography className={classes.productName}>
                                                {props.product.name}
                                            </Typography>
                                        </Grid>
                                        <Fade in timeout={1500}>
                                            <Grid
                                                container
                                                item
                                                justifyContent="flex-end"
                                                spacing={1}
                                                wrap="nowrap"
                                                style={{ width: "20%" }}
                                            >
                                                {props.product.userDetails?.canFavorite && (
                                                    <Grid item>{getFavoriteButton()}</Grid>
                                                )}
                                            </Grid>
                                        </Fade>
                                    </Grid>
                                ) : (
                                    <Typography className={classes.productName}>{props.product.name}</Typography>
                                )}
                                <Box className={classes.priceRow}>
                                    {oldNewPrice.oldPrice ? (
                                        <>
                                            <Typography variant="body2" className={classes.newPriceWithDiscount}>
                                                {oldNewPrice.price}
                                            </Typography>
                                            <Typography className={classes.oldPrice}>{oldNewPrice.oldPrice}</Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body2" className={classes.newPrice}>
                                            {oldNewPrice.price}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        {!props.expanded && (
                            <Localized id="order-food-item-quick-add">
                                <StandardButton
                                    variant="containedLight"
                                    onClick={handleQuickAdd}
                                    className={classes.buttonCollapsed}
                                >
                                    Tilføj
                                </StandardButton>
                            </Localized>
                        )}
                    </CardContent>
                    {props.product?.imageUrl ? (
                        <Fade
                            in={!props.expanded}
                            onExited={(e) => (e.style.display = "none")}
                            onEnter={(e) => (e.style.display = "block")}
                        >
                            <Grid item className={classes.cardPreviewImage}>
                                <CardMedia
                                    image={props.product.imageUrl}
                                    className={classes.cardPreviewImageMedia}
                                    classes={{ root: clsx({ [classes.disabledImage]: disabled }) }}
                                />
                                {props.product?.productDescription?.marketingTag && (
                                    <Box className={classes.marketingTag}>
                                        {props.product?.productDescription?.marketingTag}
                                    </Box>
                                )}
                                {showOfferTag && <OfferTag top="0.5rem" right="0.5rem" product={props.product} />}
                            </Grid>
                        </Fade>
                    ) : (
                        <Fade
                            in={!props.expanded}
                            onExited={(e) => (e.style.display = "none")}
                            onEnter={(e) => (e.style.display = "block")}
                        >
                            <Grid item className={clsx(classes.cardPreviewImage, classes.cardPreviewEmpty)}>
                                {props.product?.productDescription?.marketingTag && (
                                    <Box className={classes.marketingTag}>
                                        {props.product?.productDescription?.marketingTag}
                                    </Box>
                                )}
                                {showOfferTag && <OfferTag top="-1rem" right="-1rem" product={props.product} />}
                            </Grid>
                        </Fade>
                    )}
                </Grid>

                {hasMoreInfo && (
                    <>
                        <Grid item className={classes.dashedDividerContainer}>
                            <Box className={classes.dashedDivider} />
                        </Grid>
                        <Grid className={classes.secondaryInfo}>
                            <Grid item>{/* for allergens */}</Grid>
                            <Grid item>
                                {showOfferTag && <OnlyXLeftTag noImage={false} product={props.product} />}
                                {paymentMethod && (
                                    <LocalizedEnum base="receipt-payment-method" enumValue={paymentMethod}>
                                        <Typography className={classes.paymentTag}>{paymentMethod}</Typography>
                                    </LocalizedEnum>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}
            </CardActionArea>
            <ProductDisabledCover orderDetails={props.product.orderDetails} date={props.date} />
            <Collapse in={props.expanded} className={classes.cardActions}>
                <Grid container direction="column" spacing={2} className={classes.cardActionsContent}>
                    {(props.product?.description || allergensList) && (
                        <>
                            <Grid item className={classes.dashedDividerContainer}>
                                <Box className={classes.dashedDivider} />
                            </Grid>
                            <Grid item>
                                <Typography variant="body2" className={classes.description}>
                                    {props.product?.description}
                                </Typography>
                                {allergensList && (
                                    <LocalizedStrict id="order-food-allergens-list" vars={{ allergens: allergensList }}>
                                        <Typography variant="body2" className={classes.allergens}>
                                            Allergens: {allergensList}
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                            </Grid>
                        </>
                    )}
                    {!props.isMeetingPlanning && (
                        <>
                            <Grid item className={classes.dashedDividerContainer}>
                                <Box className={classes.dashedDivider} />
                            </Grid>
                            <Grid container item justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <Localized id="order-food-amount-private" vars={{ amount: orderLine.privateItems }}>
                                        <Typography variant="body2" className={classes.cardActionText}>
                                            {"Til mig"}
                                        </Typography>
                                    </Localized>
                                </Grid>
                                <Grid item>{getButton(orderLine.privateItems, updatePrivateItems)}</Grid>
                            </Grid>
                        </>
                    )}
                    {/* NOTE: ONLY show this button if product settings allow ordering to guest! */}
                    {props.product?.orderDetails?.orderToGuest && !props.isMeetingPlanning && (
                        <>
                            <Grid item className={classes.dashedDividerContainer}>
                                <Box className={classes.dashedDivider} />
                            </Grid>
                            <Grid
                                container
                                item
                                justifyContent={props?.isMeetingPlanning ? "flex-end" : "space-between"}
                                alignItems="center"
                            >
                                <Grid item>
                                    <Localized id="order-food-amount-company" vars={{ amount: orderLine.companyItems }}>
                                        <Typography variant="body2" className={classes.cardActionText}>
                                            {"Til gæst"}
                                        </Typography>
                                    </Localized>
                                </Grid>
                                <Grid item>{getButton(orderLine.companyItems, updateCompanyItems, true)}</Grid>
                            </Grid>
                        </>
                    )}
                    {/* For meeting service orders we just have one unlabelled button */}
                    {props.isMeetingPlanning && (
                        <>
                            <Grid item className={classes.dashedDividerContainer}>
                                <Box className={classes.dashedDivider} />
                            </Grid>
                            <Grid item>{getButton(orderLine.companyItems, updateCompanyItems, true)}</Grid>
                        </>
                    )}
                </Grid>
            </Collapse>
        </Card>
    )
}

export default React.memo(OrderLunchProduct)
